import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type ReactElement,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api, errMessage, newIdempotencyKey } from "../../api/client";
import { dashboardKeys } from "../../features/dashboard/useDashboard";
import { webcamCountPhrase } from "../../features/dashboard/protocol";
import { localToday } from "../../lib/date";
import { plural } from "../../lib/format";
import type { Photo, QuestionnaireOut, StudyProtocolConfig } from "../../api/types";

// Daily flow with full legacy parity (docs/archive/DESIGN.md §12a): in-browser webcam
// multi-capture OR file upload, date-taken (file mode), optional daily factors
// (sleep / stress / activity), then a "submit another day?" prompt. Photos post
// to /photos/batch; factors post as a `daily` questionnaire the batch links to.
//
// The study's protocol `config` drives the behavior: which capture methods are
// offered (a single method locks the mode), the webcam minimum / per-submission
// maximum, and whether FaceAge results are shown after submit (results-hidden
// studies — e.g. the NeuroAge competition — store estimates server-side only).

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_TRIES = 200; // first real inference is a cold model load
const SCHEMA_VERSION = "v2";
// Mirror of the server's per-photo cap (photos/schemas.py MAX_PHOTO_BYTES), so an
// oversized file is named before upload instead of failing the whole submission.
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

interface Capture {
  blob: Blob;
  url: string;
}

type Source = "webcam" | "upload";

// Track-fill percentage for a 1–5 Likert slider (app.css range styling): the
// filled bar tracks the thumb, so 1/5 sits at the start and 5/5 fills fully.
function likertFill(value: number): CSSProperties {
  return { "--fill": `${((value - 1) / 4) * 100}%` } as CSSProperties;
}

export function DailyFlow({
  active,
  onProcessed,
  config,
}: {
  active: boolean;
  onProcessed: () => Promise<void>;
  config: StudyProtocolConfig;
}): ReactElement {
  const canUpload = config.photo.methods.includes("file");
  const canWebcam = config.photo.methods.includes("webcam");
  const minWebcam = config.photo.min_webcam_photos;
  const maxPhotos = config.photo.max_photos;
  const resultsVisible = config.results_visible;

  const queryClient = useQueryClient();
  const [source, setSource] = useState<Source>(canUpload ? "upload" : "webcam");
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [capNote, setCapNote] = useState(false);
  const [dateOfPhoto, setDateOfPhoto] = useState("");

  const [wantFactors, setWantFactors] = useState(false);
  const [sleepHours, setSleepHours] = useState("");
  const [sleepQuality, setSleepQuality] = useState(3);
  const [stress, setStress] = useState(3);
  const [activity, setActivity] = useState("");
  const [activityOther, setActivityOther] = useState("");
  const [duration, setDuration] = useState("");

  const [camOn, setCamOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submittedAnother, setSubmittedAnother] = useState(false);
  const [results, setResults] = useState<Photo[]>([]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const today = localToday();
  const photoCount = source === "webcam" ? captures.length : files.length;
  const enough = source === "webcam" ? photoCount >= minWebcam : photoCount >= 1;
  const needDate = source === "upload" && files.length > 0 && !dateOfPhoto;
  const oversize = files.filter((f) => f.size > MAX_PHOTO_BYTES);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  }, []);

  async function startCam(): Promise<void> {
    setError(null);
    if (!window.isSecureContext) {
      setError(
        "Camera capture needs a secure (HTTPS) connection. Open this site over HTTPS or use file upload.",
      );
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Your browser does not support camera capture. Use file upload instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamOn(true);
    } catch {
      setError("Could not access the camera. Check permissions or use file upload.");
    }
  }

  function capture(): void {
    const video = videoRef.current;
    if (!video || captures.length >= maxPhotos) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) setCaptures((prev) => [...prev, { blob, url: URL.createObjectURL(blob) }]);
    }, "image/jpeg", 0.9);
  }

  function resetCaptures(): void {
    captures.forEach((c) => URL.revokeObjectURL(c.url));
    setCaptures([]);
  }

  // Each picker trip APPENDS to the selection (OS pickers replace their own
  // FileList, so participants adding photos one at a time would otherwise lose
  // the previous picks). Dedupe re-picks; clearing the input value lets the
  // same file fire `change` again after a remove.
  function onFiles(e: ChangeEvent<HTMLInputElement>): void {
    const list = e.target.files;
    if (!list) return;
    const merged = [...files];
    for (const f of Array.from(list)) {
      const dup = merged.some(
        (m) => m.name === f.name && m.size === f.size && m.lastModified === f.lastModified,
      );
      if (!dup) merged.push(f);
    }
    setCapNote(merged.length > maxPhotos);
    setFiles(merged.slice(0, maxPhotos));
    e.target.value = "";
  }

  function removeFile(index: number): void {
    setCapNote(false);
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function resetForAnother(): void {
    resetCaptures();
    setFiles([]);
    setCapNote(false);
    setDateOfPhoto("");
    setWantFactors(false);
    setSubmittedAnother(false);
    setResults([]);
    setLog(null);
    setError(null);
  }

  // Poll the batch to terminal states, keeping the final records so the result
  // (FaceAge / rejection) can be shown inline. One request per tick for the
  // whole batch (the list endpoint's `ids` filter), not a GET per photo.
  // Returns them in submission order.
  async function pollAll(ids: number[]): Promise<Photo[]> {
    const pending = new Set(ids);
    const finished = new Map<number, Photo>();
    for (let i = 0; i < POLL_MAX_TRIES && pending.size > 0; i++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const photos = await api<Photo[]>(
        "GET",
        `/participants/me/photos?ids=${[...pending].join(",")}`,
      );
      for (const photo of photos) {
        if (photo.status === "processed" || photo.status === "rejected" || photo.status === "failed") {
          pending.delete(photo.id);
          finished.set(photo.id, photo);
        }
      }
      setLog(`Processing… ${ids.length - pending.size}/${ids.length} done.`);
    }
    await onProcessed();
    return ids.map((id) => finished.get(id)).filter((p): p is Photo => p !== undefined);
  }

  async function submit(): Promise<void> {
    if (!enough) {
      setError(
        source === "webcam"
          ? `Capture ${minWebcam === maxPhotos ? "all " : "at least "}${plural(minWebcam, "photo")}.`
          : "Choose at least one photo.",
      );
      return;
    }
    if (source === "upload" && !dateOfPhoto) {
      setError("Enter the date these photos were taken.");
      return;
    }
    // The daily block is not a <form>, so the inputs' min/max never fire —
    // enforce the humanly-possible ranges here (the API rejects them too).
    if (wantFactors) {
      const sleep = sleepHours ? Number(sleepHours) : null;
      if (sleep !== null && (!Number.isFinite(sleep) || sleep < 0 || sleep > 24)) {
        setError("Hours of sleep must be between 0 and 24.");
        return;
      }
      const hours = duration ? Number(duration) : null;
      if (hours !== null && (!Number.isFinite(hours) || hours < 0 || hours > 24)) {
        setError("Activity duration must be between 0 and 24 hours.");
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      // 1. Optional daily factors → a `daily` questionnaire the photos link to.
      let questionnaireId: number | undefined;
      if (wantFactors) {
        const payload: Record<string, unknown> = {
          sleep_hours: sleepHours ? Number(sleepHours) : null,
          sleep_quality: sleepQuality,
          stress_level: stress,
          activity_type: activity || null,
          ...(activity === "other" ? { activity_other: activityOther } : {}),
          ...(activity && activity !== "none" ? { activity_duration_hours: Number(duration) || null } : {}),
        };
        const q = await api<QuestionnaireOut>("POST", "/participants/me/questionnaire", {
          json: { questionnaire_type: "daily", payload, schema_version: SCHEMA_VERSION },
        });
        questionnaireId = q.id;
      }

      // 2. Upload the photos in one batch.
      const form = new FormData();
      if (source === "webcam") {
        captures.forEach((c, i) => form.append("files", c.blob, `capture_${i}.jpg`));
        // Webcam captures are taken now — stamp the participant's local date so
        // the dashboard shows the day they took the photo, not the server's.
        form.append("date_of_photo", today);
        form.append("capture_method", "webcam");
      } else {
        files.forEach((f) => form.append("files", f));
        form.append("date_of_photo", dateOfPhoto);
        form.append("capture_method", "file");
      }
      if (questionnaireId !== undefined) {
        form.append("questionnaire_response_id", String(questionnaireId));
      }
      setLog("Uploading…");
      // One key per submission attempt: a network retry of the same submit
      // can't create duplicate photo rows server-side.
      const photos = await api<Photo[]>("POST", "/participants/me/photos/batch", {
        form,
        headers: { "Idempotency-Key": newIdempotencyKey() },
      });
      // Optimistic gallery: the rows are real (the server just returned them),
      // so show them in their processing state immediately rather than waiting
      // for the poll + refetch to land.
      queryClient.setQueryData<Photo[]>(dashboardKeys.photos, (old) =>
        old ? [...photos, ...old] : old,
      );
      stopCam();
      // Results-hidden studies never show FaceAge — skip polling/display; the
      // estimate is still computed and stored server-side for the export.
      if (!resultsVisible) {
        setLog(null);
        setSubmittedAnother(true);
      } else {
        setLog(`Uploaded ${photos.length} photo(s). Running inference…`);
        const finished = await pollAll(photos.map((p) => p.id));
        setResults(finished);
        setSubmittedAnother(true);
      }
    } catch (err) {
      setError(errMessage(err, "Submission failed."));
      setLog(null);
    } finally {
      setBusy(false);
    }
  }

  if (submittedAnother) {
    // Results-hidden studies never see FaceAge results; everyone else gets the
    // per-photo list + Trends hint. Single done-block for both.
    const processed = results.filter((p) => p.estimate);
    return (
      <div className="daily-done">
        <p className="form-ok">
          <i className="fas fa-check-circle" aria-hidden="true" /> Submission received
          {resultsVisible ? "." : ", recorded for the study."}
        </p>
        {resultsVisible &&
          (results.length > 0 ? (
            <ul className="photo-list">
              {results.map((p) => (
                <li key={p.id} className="photo-row">
                  {p.download_url ? (
                    <img
                      className="thumb"
                      src={p.thumb_url ?? p.download_url}
                      alt={`photo ${p.id}`}
                      loading="lazy"
                    />
                  ) : (
                    <span className="thumb thumb-empty" />
                  )}
                  <span className="photo-id">#{p.id}</span>
                  <span className="photo-age">
                    {p.estimate ? `${p.estimate.estimated_age} yrs` : "—"}
                  </span>
                  <span className={`status-pill ${p.status}`}>
                    {p.status}
                    {p.rejection_reason ? `: ${p.rejection_reason}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Still processing. Check the Trends tab in a moment.</p>
          ))}
        {resultsVisible && processed.length > 0 ? (
          <p className="field-hint">See your FaceAge over time in the Trends tab.</p>
        ) : null}
        <button className="btn-ghost" onClick={resetForAnother}>
          Submit another day
        </button>
      </div>
    );
  }

  return (
    <div className="daily-flow">
      <p className="muted">
        {!canUpload
          ? `Capture ${webcamCountPhrase(config.photo)} webcam photos from today. A clear, single, well-lit face works best.`
          : `Upload or capture ${minWebcam}-${maxPhotos} photos from a single day. A clear, single, well-lit face works best.`}
      </p>

      {canUpload && canWebcam ? (
      <div className="seg">
        <button
          className={`seg-btn${source === "upload" ? " active" : ""}`}
          onClick={() => {
            stopCam();
            setSource("upload");
          }}
          disabled={!active}
        >
          Upload files
        </button>
        <button
          className={`seg-btn${source === "webcam" ? " active" : ""}`}
          onClick={() => setSource("webcam")}
          disabled={!active}
        >
          Use webcam
        </button>
      </div>
      ) : null}

      {source === "webcam" ? (
        <div className="webcam">
          <div className="webcam-stage">
            <video ref={videoRef} playsInline muted className="webcam-video" />
            <div className="webcam-guide" aria-hidden="true" />
          </div>
          <div className="inline-form">
            {!camOn ? (
              <button className="btn-ghost" onClick={startCam} disabled={!active || busy}>
                Start camera
              </button>
            ) : (
              <>
                <button className="btn-ghost" onClick={stopCam} disabled={busy}>
                  Stop
                </button>
                <button
                  className="btn-primary-glow"
                  onClick={capture}
                  disabled={busy || captures.length >= maxPhotos}
                >
                  Capture ({captures.length}/{maxPhotos})
                </button>
              </>
            )}
            {captures.length > 0 ? (
              <button className="btn-ghost btn-sm" onClick={resetCaptures} disabled={busy}>
                Reset
              </button>
            ) : null}
          </div>
          {captures.length > 0 ? (
            <div className="thumb-gallery">
              {captures.map((c, i) => (
                <img key={i} src={c.url} alt={`capture ${i + 1}`} className="thumb" />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="upload-mode">
          <input
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
            multiple
            disabled={!active || busy}
            onChange={onFiles}
          />
          {capNote ? (
            <p className="field-hint">
              Only the first {maxPhotos} photos were kept; {maxPhotos} per submission is the
              maximum.
            </p>
          ) : null}
          {files.length > 0 ? (
            <ul className="file-list">
              {files.map((f, i) => (
                <li key={i}>
                  {f.name} <span className="muted">({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
                  <button
                    type="button"
                    className="file-remove"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => removeFile(i)}
                    disabled={busy}
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <label className="field">
            <span>Date photos were taken (required)</span>
            <input type="date" max={today} value={dateOfPhoto} onChange={(e) => setDateOfPhoto(e.target.value)} />
          </label>
        </div>
      )}

      {/* Optional daily factors. */}
      <label className="choice daily-toggle">
        <input type="checkbox" checked={wantFactors} onChange={(e) => setWantFactors(e.target.checked)} />
        Add today's optional daily questionnaire (sleep, stress, activity)
      </label>
      {wantFactors ? (
        <div className="consent-block">
          <label className="field">
            <span>Hours of sleep last night</span>
            <input
              type="number"
              min={0}
              max={24}
              step={0.5}
              placeholder="Enter hours (0-24)"
              value={sleepHours}
              onChange={(e) => setSleepHours(e.target.value)}
            />
            <div className="field-hint">Please enter hours to the nearest half hour (e.g., 7.5)</div>
          </label>
          <label className="field">
            <span>Sleep quality: {sleepQuality}/5</span>
            <input
              type="range"
              min={1}
              max={5}
              value={sleepQuality}
              style={likertFill(sleepQuality)}
              onChange={(e) => setSleepQuality(Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span>Stress level: {stress}/5</span>
            <input
              type="range"
              min={1}
              max={5}
              value={stress}
              style={likertFill(stress)}
              onChange={(e) => setStress(Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span>Physical activity</span>
            <select value={activity} onChange={(e) => setActivity(e.target.value)}>
              <option value="">Select…</option>
              <option value="none">None</option>
              <option value="walking">Walking</option>
              <option value="running">Running</option>
              <option value="swimming">Swimming</option>
              <option value="weightlifting">Weightlifting</option>
              <option value="yoga">Yoga</option>
              <option value="biking">Biking</option>
              <option value="other">Other</option>
            </select>
          </label>
          {activity === "other" ? (
            <label className="field">
              <span>Specify activity</span>
              <input type="text" maxLength={100} placeholder="Enter activity type" value={activityOther} onChange={(e) => setActivityOther(e.target.value)} />
            </label>
          ) : null}
          {activity && activity !== "none" ? (
            <label className="field">
              <span>Duration (hours)</span>
              <input type="number" min={0} max={24} step={0.5} value={duration} onChange={(e) => setDuration(e.target.value)} />
              <div className="field-hint">Enter the duration in hours (e.g., 1.5 for 1 hour and 30 minutes)</div>
            </label>
          ) : null}
        </div>
      ) : null}

      {log ? <p className="form-ok">{log}</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
      {needDate ? (
        <p className="field-hint">Enter the date the photos were taken to submit.</p>
      ) : null}
      {oversize.length > 0 ? (
        <p className="field-hint">
          Over the 10 MB per-photo limit: {oversize.map((f) => f.name).join(", ")}. Remove{" "}
          {oversize.length === 1 ? "it" : "them"} to submit.
        </p>
      ) : null}
      {source === "webcam" && captures.length < minWebcam ? (
        <p className="field-hint">
          Capture {minWebcam === maxPhotos ? "all" : "at least"} {plural(minWebcam, "photo")} to
          submit ({captures.length}/{minWebcam} so far).
        </p>
      ) : null}
      <button
        className="btn-primary-glow"
        onClick={submit}
        disabled={!active || busy || !enough || needDate || oversize.length > 0}
      >
        {busy ? "Working…" : "Submit photos"}
      </button>
    </div>
  );
}
